/* Лист шаблона так, как его строит сам прототип (preHTML в assets/bot-studio.js): строки по формату — запас на случай, когда
   документ шаблона (§6.3) их не несёт. Строка сервера всегда главнее; нет её — та же строка, что у прототипа:
   сюжет PRE_D, слой «до» и подписи кнопок под кадром baBefore / BA_AFTER, строки «Что нужно» needRows / NEED_F, подпись роли
   roleSub, подвал needHTML, кнопка «Выбрать, кто играет» у шаблона с людьми (appRoles). Все строки — через uiText / uiFormat. */
import { accountLocale, uiFormat, uiText } from '../../i18n';
import type { StudioPreviewModel, StudioPreviewRole } from './studio-preview-model';

/** Кадров в шаблоне-фотосессии — BotShoot.APP_SHOTS прототипа */
const SHOTS = 3;
/** Форма слова при числе (plural прототипа): по правилам языка профиля — ключ формы «один», «несколько» или «много»
    (у английского «21 style» не бывает: у него «один» — только 1); без Intl — русские правила */
const plural = (n: number, one: string, few: string, many: string) => {
  try {
    const form = new Intl.PluralRules(accountLocale()).select(n);
    return form === 'one' ? one : form === 'few' ? few : many;
  } catch {
    const a = Math.abs(n) % 100,
      b = a % 10;
    return a > 10 && a < 20 ? many : b > 1 && b < 5 ? few : b === 1 ? one : many;
  }
};
/** «4 стиля» (styN) */
const styleCount = (k: number) => uiFormat(plural(k, '{0} стиль', '{0} стиля', '{0} стилей'), k);
const cap1 = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
/** BotCore.mmss: 12 → «0:12» */
const mmss = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Слой «до» (baBefore): ваше фото, круги ролей сцены, заготовка текста (кружок) или темы (слайды) */
export type StudioBeforeKind = 'photo' | 'people' | 'text' | 'topic';
export const BEFORE_KINDS: readonly StudioBeforeKind[] = ['photo', 'people', 'text', 'topic'];

/** Роль, как её видит лист прототипа (appRoles): k — кто встаёт в роль (взрослый, ребёнок, питомец), g — пол, req — обязательна */
export interface TemplateRole {
  id: string;
  name: string;
  k: 'man' | 'kid' | 'pet';
  g: 'm' | 'f' | null;
  req: boolean;
  max: number;
  allowChar: boolean;
}
function templateRole(
  role: Pick<StudioPreviewRole, 'id' | 'name' | 'kinds' | 'gender' | 'min' | 'max' | 'allowChar' | 'required'>,
): TemplateRole {
  const kinds = role.kinds ?? [];
  const man = !kinds.length || kinds.includes('adult') || kinds.includes('old');
  const min = role.min ?? (role.required === false ? 0 : 1);
  return {
    id: role.id,
    name: role.name,
    k: man ? 'man' : kinds.includes('kid') ? 'kid' : 'pet',
    g: role.gender ?? null,
    req: min > 0,
    max: Math.max(min, 1, role.max ?? 1),
    allowChar: role.allowChar ?? man,
  };
}
/** Люди шаблона (appRoles): роли документа; у Reels без своих ролей (§6.3: cast: []) — одна роль «Вы» на одного; у остальных
    форматов людей нет. Имя роли видно подписью круга слоя «до» — через uiText */
export function templateRoles(model: Pick<StudioPreviewModel, 'roles' | 'format'>): TemplateRole[] {
  if (model.roles.length) return model.roles.map(templateRole);
  return model.format === 'reels' ? [templateRole({ id: 'me', name: uiText('Вы'), min: 1, max: 1 })] : [];
}

/** Подпись роли (roleSub): кто может играть; необязательная — «По желанию: …» */
export function roleSubtitle(role: TemplateRole) {
  const key = role.allowChar
    ? 'Вы, близкие или персонаж'
    : role.k === 'kid'
      ? role.max > 1
        ? 'Ваши дети, до {0}'
        : 'Ребёнок, одно фото'
      : role.k === 'pet'
        ? 'Питомец, одно фото'
        : 'Вы или близкие, одно фото';
  return uiFormat(role.req ? key : `По желанию: ${key.charAt(0).toLowerCase()}${key.slice(1)}`, role.max);
}

const BEFORE_LABEL: Record<StudioBeforeKind, string> = { photo: 'Ваше фото', people: 'Ваши фото', text: 'Ваш текст', topic: 'Ваша тема' };
/** Подпись заготовки слоя «до» (.pre-ph.tx small) */
export const PLACEHOLDER_CAPTION: Partial<Record<StudioBeforeKind, string>> = { text: 'Ваш текст', topic: 'Тема поста' };
const AFTER_LABEL: Record<string, string> = {
  reels: 'Готовый ролик',
  video: 'Готовый ролик',
  circle: 'Готовый кружок',
  slides: 'Готовые слайды',
  photo: 'Готовые кадры',
  post: 'Готовое фото',
  avatar: 'Готовая аватарка',
};
/** Что нужно шаблону без людей (NEED_F, NEED_IC): значок, что, подсказка; у кружка — текст до 250 знаков */
const NEED: Record<string, [icon: string, title: string, hint: string]> = {
  post: ['face', '1 фото', 'Любое, где хорошо видно лицо'],
  avatar: ['face', '1 фото', 'Лицо крупно и при хорошем свете'],
  photo: ['face', '1 фото', 'Селфи, лицо крупно'],
  video: ['image', '1 фото', 'Снимок оживёт в ролике'],
  circle: ['text', 'Текст', 'Что скажет герой, до 250 знаков'],
  slides: ['doc', 'Тема поста', 'Или фото, из которых сделаем слайды'],
};
/** Формат словом в подвале карточки (FMT[f][0]) */
const FORMAT_WORD: Record<string, string> = {
  reels: 'Reels',
  post: 'Пост',
  photo: 'Фотосессия',
  video: 'Видео',
  circle: 'Кружок',
  avatar: 'Аватар',
  slides: 'Слайды',
};

/** Строка под названием по формату (PRE_D); число стилей не пришло — у форматов, которым оно нужно, строки нет */
function templatePlot(format: string | undefined, k: number | undefined) {
  if (format === 'reels') return uiText('Готовый сюжет с вами: монтаж и музыка уже в шаблоне');
  if (format === 'photo')
    return uiFormat(
      plural(
        SHOTS,
        'Фотосессия из {0} кадра в стиле шаблона',
        'Фотосессия из {0} кадров в стиле шаблона',
        'Фотосессия из {0} кадров в стиле шаблона',
      ),
      SHOTS,
    );
  if (k === undefined) return undefined;
  const line: Record<string, string> = {
    post: 'Пост в стиле шаблона, {0} на выбор',
    avatar: 'Аватарка для соцсетей и мессенджеров, {0} на выбор',
    video: 'Ваш снимок оживёт в коротком ролике, {0} на выбор',
    circle: 'Персонаж скажет ваш текст в кружке Telegram, {0} на выбор',
    slides: 'Карусель слайдов для поста, {0} на выбор',
  };
  return format && line[format] ? uiFormat(line[format], styleCount(k)) : undefined;
}
/** Строки «Что нужно» без ролей (needRows); у Reels без своих ролей — «1 человек · Вы, близкие или персонаж» */
function templateRows(model: StudioPreviewModel, people: TemplateRole[]) {
  if (people.length) return [{ icon: 'user', title: cap1(model.needText ?? uiText('1 человек')), hint: roleSubtitle(people[0]) }];
  const format = model.format ?? '',
    k = model.stylesCount ?? 0;
  /* формат без своих строк (text, audio, неизвестный) строк поста не получает: «1 фото, где видно лицо» у них неправда */
  const need = Object.hasOwn(NEED, format) ? NEED[format] : undefined;
  if (!need) return [];
  const [icon, title, hint] = need;
  const rows = [{ icon, title: uiText(title), hint: uiText(hint) }];
  if (format !== 'photo' && k > 1)
    rows.push({
      icon: 'wand',
      title: uiText(format === 'circle' ? 'Стиль кружка' : 'Стиль'),
      hint: uiFormat('{0}, выберете на следующем шаге', styleCount(k)),
    });
  return rows;
}
/** Подвал карточки: формат, длительность ролика, число кадров фотосессии («Reels · 0:12», «Фотосессия · 3 кадра») */
function templateMeta(format: string | undefined, duration: number | undefined) {
  const word = format ? FORMAT_WORD[format] : undefined;
  if (!word) return undefined;
  const shots = format === 'photo' ? uiFormat(plural(SHOTS, '{0} кадр', '{0} кадра', '{0} кадров'), SHOTS) : '';
  return [uiText(word), duration ? mmss(duration) : '', shots].filter(Boolean).join(' · ');
}

/** Что показывает лист: значения документа, где их нет — строки прототипа по формату */
export interface StudioPreviewLook {
  /** люди шаблона (appRoles): круги слоя «до» сцены */
  people: TemplateRole[];
  /** кнопка «Выбрать, кто играет» — роли у документа, open_role у входа или (поля нет) шаблон Reels */
  cast: boolean;
  beforeKind: StudioBeforeKind;
  beforeLabel: string;
  afterLabel: string;
  plot?: string;
  rows: Array<{ icon: string; title: string; hint?: string }>;
  meta?: string;
  /** подпись строки роли: своя у документа или roleSub */
  subtitle(role: StudioPreviewRole): string;
}
export function previewLook(model: StudioPreviewModel): StudioPreviewLook {
  const people = templateRoles(model);
  /* вид слоя «до» не пришёл — по формату (baBefore); заготовка текста или темы — только когда своего «до» сервер не прислал:
     присланный before_url главнее правила формата */
  const placeholder = !model.before && (model.format === 'circle' ? 'text' : model.format === 'slides' ? 'topic' : undefined);
  const beforeKind: StudioBeforeKind = model.beforeKind ?? (placeholder || (people.length > 1 ? 'people' : 'photo'));
  /* лист из карточки: строк документа ещё нет — места под них держит .pre-nd-wait, строки по формату не подставляются */
  const draft = !!model.provisional;
  return {
    people,
    cast: model.roles.length > 0 || (model.openRole ?? people.length > 0),
    beforeKind,
    beforeLabel: model.beforeLabel ?? uiText(BEFORE_LABEL[beforeKind]),
    afterLabel: model.afterLabel ?? uiText((model.format && AFTER_LABEL[model.format]) || 'Результат'),
    plot: model.plot ?? (draft ? undefined : templatePlot(model.format, model.stylesCount)),
    /* строки сервера главнее; пустой список — как нет поля (§6.3: rows: [] у сцены, строки которой — роли из cast):
       карточка «Что нужно» шаблона пустой не бывает, у шаблона без ролей — строки по формату */
    rows: model.needs?.length
      ? model.needs.map(row => ({ ...row, icon: row.icon ?? needIcon(row.title, model.format) }))
      : draft || model.roles.length
        ? []
        : templateRows(model, people),
    meta: model.meta ?? (draft ? undefined : templateMeta(model.format, model.duration)),
    subtitle: role => role.subtitle ?? roleSubtitle(templateRole(role)),
  };
}

/** Значок строки «Что нужно» без своего значка у сервера — по смыслу строки и формату шаблона (NEED_IC эталона) */
export function needIcon(title: string, format?: string) {
  if (/стил|образ/i.test(title)) return 'wand';
  if (/текст/i.test(title)) return 'text';
  if (/тем/i.test(title)) return 'doc';
  if (/человек|ребён|дет|питом/i.test(title)) return 'user';
  return { circle: 'text', photo: 'face', post: 'face', avatar: 'face', video: 'image', slides: 'doc' }[format ?? ''] ?? 'image';
}
